/**
 * Pure selection logic behind the recruiter compare view: grouping completed
 * invites by role title, the deterministic row order that makes sort ties
 * stable, and the union of section names for the per-section columns.
 */

import type { InviteOut } from '@/api/client'

export interface TitleGroup {
  title: string
  invites: InviteOut[]
}

/**
 * Completed invites grouped by identical title (a title names a role; invites
 * under different titles are different hiring bars and never mix). Groups come
 * back largest-first so the picker defaults to the role with the most
 * candidates; equal sizes fall back to alphabetical for a stable order.
 */
export function groupCompletedByTitle(invites: InviteOut[]): TitleGroup[] {
  const groups = new Map<string, InviteOut[]>()
  for (const invite of invites) {
    if (invite.status !== 'completed') continue
    const bucket = groups.get(invite.title)
    if (bucket) bucket.push(invite)
    else groups.set(invite.title, [invite])
  }
  return [...groups.entries()]
    .map(([title, groupInvites]) => ({ title, invites: groupInvites }))
    .sort((a, b) => b.invites.length - a.invites.length || a.title.localeCompare(b.title))
}

/**
 * The feed order the table receives: oldest completed first, then invite id —
 * a total order, so the table's stable sort renders deterministic ties (the
 * candidate who finished earliest keeps the higher rank).
 */
export function orderRowsForFeed(invites: InviteOut[]): InviteOut[] {
  return [...invites].sort((a, b) => {
    const at = new Date(a.completed_at ?? a.created_at).getTime()
    const bt = new Date(b.completed_at ?? b.created_at).getTime()
    return at - bt || a.invite_id.localeCompare(b.invite_id)
  })
}

/** Section names across a group, in first-seen order, for the table columns. */
export function sectionColumns(invites: InviteOut[]): string[] {
  const names: string[] = []
  for (const invite of orderRowsForFeed(invites)) {
    for (const name of Object.keys(invite.section_scores ?? {})) {
      if (!names.includes(name)) names.push(name)
    }
  }
  return names
}

/**
 * Rank for sorting recommendations best-first, not the alphabet — "No Hire"
 * would otherwise out-rank "Hire". Null sorts as "no verdict" (table blanks
 * last).
 */
export function recommendationRank(rec: InviteOut['recommendation']): number | null {
  switch (rec) {
    case 'Strong Hire': return 3
    case 'Hire': return 2
    case 'Hold': return 1
    case 'No Hire': return 0
    default: return null
  }
}
